import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { mergeExistingFields } from "helpers/mergeExistingFields";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export interface MultiScopeItem {
  id: number;
  title: string;
  slug: string;
  isActive: boolean;
  image: string | null;
  icon: string | null;
  items: number;
}

export interface MultiScopePayload {
  id?: number;
  title: string;
  slug: string;
  isActive: boolean;
  image?: string | File | null;
  icon?: string | File | null;
  removeImage?: boolean;
  removeIcon?: boolean;
}

/** A Multi Scope has a separate title and activation state for each locale. */
export interface MultiScopeTranslationPayload {
  locale: string;
  title?: string | null;
  isActive?: boolean;
}

export type MultiScopeInstanceType = "tag" | "collection";

export interface MultiScopeCompositionItem {
  instanceType: MultiScopeInstanceType;
  instanceId: number;
  priorityOrder: number;
  title: string;
  slug?: string;
  image?: string | null;
}

interface MultiScopeState {
  list: MultiScopeItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  selectedItems: number[];
  processingItemId?: number;
}

interface GetListPayload {
  limit: number;
  offset: number;
  title?: string;
  isActive?: number;
}

export const getMultiScopeList = createAsyncThunk(
  "game-center/multi-scope/list",
  async (params: GetListPayload) => {
    const result = await instance.get(apiRoutes.multiScopeList, { params });
    const payload = result.data?.data ?? result.data;

    return {
      ...payload,
      // Keep this explicit order: Table derives its columns from object keys.
      items: (payload?.items ?? []).map(
        (item: Omit<MultiScopeItem, "items"> & { items?: unknown[] }) => ({
          id: item.id,
          image: item.image,
          icon: item.icon,
          title: item.title,
          slug: item.slug,
          items: item.items?.length ?? 0,
          isActive: item.isActive,
        }),
      ),
    };
  },
);

export const deleteMultiScopeItem = createAsyncThunk(
  "game-center/multi-scope/delete",
  async (id: number) => {
    const result = await instance.delete(`${apiRoutes.multiScopeDelete}/${id}`);
    return result.data;
  },
);

const buildRequestData = (payload: MultiScopePayload) => {
  const hasFiles =
    payload.image instanceof File || payload.icon instanceof File;
  const values = {
    title: payload.title,
    slug: payload.slug,
    isActive: payload.isActive,
    ...(payload.removeImage ? { removeImage: true } : {}),
    ...(payload.removeIcon ? { removeIcon: true } : {}),
  };

  if (!hasFiles) return values;

  const data = new FormData();
  data.append("title", payload.title);
  data.append("slug", payload.slug);
  data.append("isActive", payload.isActive ? "1" : "0");
  if (payload.removeImage) data.append("removeImage", "true");
  if (payload.removeIcon) data.append("removeIcon", "true");
  if (payload.image instanceof File) data.append("image", payload.image);
  if (payload.icon instanceof File) data.append("icon", payload.icon);
  return data;
};

export const getMultiScopeItem = createAsyncThunk<MultiScopePayload, number>(
  "game-center/multi-scope/read",
  async (id) => {
    const result = await instance.get(`${apiRoutes.multiScopeBase}/${id}`);
    return result.data?.data ?? result.data;
  },
);

export const getMultiScopeComposition = createAsyncThunk<
  MultiScopeCompositionItem[],
  number,
  { rejectValue: ApiErrorPayload }
>("game-center/multi-scope/items/read", async (id, { rejectWithValue }) => {
  try {
    const result = await instance.get(
      `${apiRoutes.multiScopeBase}/${id}/items`,
    );
    const data = result.data?.data ?? result.data;
    return data?.items ?? [];
  } catch (error: any) {
    return rejectWithValue(error?.response?.data ?? error?.data ?? error);
  }
});

export const updateMultiScopeComposition = createAsyncThunk<
  void,
  { id: number; items: MultiScopeCompositionItem[] },
  { rejectValue: ApiErrorPayload }
>(
  "game-center/multi-scope/items/update",
  async ({ id, items }, { rejectWithValue }) => {
    try {
      await instance.put(`${apiRoutes.multiScopeBase}/${id}/items`, {
        items: items.map(({ instanceType, instanceId, priorityOrder }) => ({
          instanceType,
          instanceId,
          priorityOrder,
        })),
      });
    } catch (error: any) {
      return rejectWithValue(error?.response?.data ?? error?.data ?? error);
    }
  },
);

export const getMultiScopeTranslation = createAsyncThunk<
  MultiScopeTranslationPayload | undefined,
  { id: number; locale: string },
  { rejectValue: ApiErrorPayload }
>(
  "game-center/multi-scope/translation/read",
  async ({ id, locale }, { rejectWithValue }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.multiScopeBase}/${id}/translation/${locale}`,
      );
      const data = result.data?.data ?? result.data;
      return {
        locale,
        title: data?.title ?? "",
        isActive: Boolean(data?.isActive),
      };
    } catch (error: any) {
      // A missing translation is a valid empty form state, not an API error.
      if (error?.response?.status === 404) return undefined;
      return rejectWithValue(error?.response?.data ?? error?.data ?? error);
    }
  },
);

export const updateMultiScopeTranslation = createAsyncThunk<
  void,
  { id: number; payload: MultiScopeTranslationPayload },
  { rejectValue: ApiErrorPayload }
>(
  "game-center/multi-scope/translation/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const { locale, ...changes } = payload;
      await instance.put(
        `${apiRoutes.multiScopeBase}/${id}/translation/${locale}`,
        changes,
      );
    } catch (error: any) {
      return rejectWithValue(error?.response?.data ?? error?.data ?? error);
    }
  },
);

export const createMultiScope = createAsyncThunk<
  number,
  MultiScopePayload,
  { rejectValue: ApiErrorPayload }
>("game-center/multi-scope/create", async (payload, { rejectWithValue }) => {
  try {
    const data = buildRequestData(payload);
    const result = await instance.post(
      apiRoutes.multiScopeCreate,
      data,
      data instanceof FormData
        ? {
            // apiClient defaults to application/json; this prevents Axios from serializing FormData as JSON.
            // Axios clears this header in the browser and lets it add the multipart boundary.
            headers: { "Content-Type": "multipart/form-data" },
          }
        : undefined,
    );
    const responseData = result.data?.data ?? result.data;
    return responseData.id;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data ?? error?.data ?? error);
  }
});

export const updateMultiScope = createAsyncThunk<
  void,
  MultiScopePayload,
  { rejectValue: ApiErrorPayload }
>("game-center/multi-scope/update", async (payload, { rejectWithValue }) => {
  try {
    const data = buildRequestData(payload);
    await instance.put(
      `${apiRoutes.multiScopeUpdate}/${payload.id}`,
      data,
      data instanceof FormData
        ? {
            // See create: override the JSON default before Axios transforms the FormData.
            headers: { "Content-Type": "multipart/form-data" },
          }
        : undefined,
    );
  } catch (error: any) {
    return rejectWithValue(error?.response?.data ?? error?.data ?? error);
  }
});

const initialState: MultiScopeState = {
  list: [],
  activePage: 1,
  totalPages: 0,
  totalItems: 0,
  isListFetched: false,
  selectedItems: [],
};

export const multiScopeSlice = createSlice({
  name: "multiScope",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.selectedItems = [];
      state.isListFetched = false;
    },
    handleInvalidateList: (state) => {
      state.isListFetched = false;
    },
    handleSelectItem: (state, action: PayloadAction<number>) => {
      state.selectedItems = state.selectedItems.includes(action.payload)
        ? state.selectedItems.filter((id) => id !== action.payload)
        : [...state.selectedItems, action.payload];
    },
    handleSelectAll: (state) => {
      state.selectedItems =
        state.selectedItems.length === state.list.length
          ? []
          : state.list.map((item) => item.id);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getMultiScopeList.pending, (state) => {
      state.isListFetched = false;
    });
    builder.addCase(getMultiScopeList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload.items;
      state.totalItems = action.payload.totalItems ?? 0;
      state.totalPages = action.payload.totalPages ?? 0;
    });
    builder.addCase(getMultiScopeList.rejected, (state) => {
      state.isListFetched = true;
    });
    builder.addCase(updateMultiScope.pending, (state, action) => {
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateMultiScope.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, {
              ...action.meta.arg,
              image:
                action.meta.arg.image instanceof File
                  ? item.image
                  : action.meta.arg.image ?? item.image,
              icon:
                action.meta.arg.icon instanceof File
                  ? item.icon
                  : action.meta.arg.icon ?? item.icon,
            })
          : item,
      );
      state.processingItemId = undefined;
    });
    builder.addCase(updateMultiScope.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updateMultiScopeComposition.pending, (state, action) => {
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateMultiScopeComposition.fulfilled, (state, action) => {
      state.list = state.list.map((item) =>
        item.id === action.meta.arg.id
          ? mergeExistingFields(item, { items: action.meta.arg.items.length })
          : item,
      );
      state.processingItemId = undefined;
    });
    builder.addCase(updateMultiScopeComposition.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(updateMultiScopeTranslation.pending, (state, action) => {
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(updateMultiScopeTranslation.fulfilled, (state, action) => {
      if (action.meta.arg.payload.title !== undefined) {
        state.list = state.list.map((item) =>
          item.id === action.meta.arg.id
            ? mergeExistingFields(item, {
                title: action.meta.arg.payload.title ?? "",
              })
            : item,
        );
      }
      state.processingItemId = undefined;
    });
    builder.addCase(updateMultiScopeTranslation.rejected, (state) => {
      state.processingItemId = undefined;
    });
    builder.addCase(deleteMultiScopeItem.pending, (state, action) => {
      state.processingItemId = action.meta.arg;
    });
    builder.addCase(deleteMultiScopeItem.fulfilled, (state) => {
      state.processingItemId = undefined;
      state.isListFetched = false;
    });
    builder.addCase(deleteMultiScopeItem.rejected, (state) => {
      state.processingItemId = undefined;
    });
  },
});

export const {
  handlePaginate,
  handleInvalidateList,
  handleSelectAll,
  handleSelectItem,
} = multiScopeSlice.actions;
